import React, {useEffect, useState} from 'react';
import useAuth from "../../hooks/useAuth";
import {fillNamesOfSumDays, getAllProducts, getAllProductsGlobal} from "./common";
import {
    Avatar,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Stack,
    SvgIcon,
    Typography,
    useMediaQuery
} from "@mui/material";
import ApexChart from "react-apexcharts";
import {formatEurPriceAndRoundUtil} from "../../utils/currencyUtil";
import {getFilterMoment} from "../../utils/DateUtil";
import localStrings from "../../localStrings";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import EuroIcon from "@mui/icons-material/Euro";
import StackStatPresenter from "../../components/StackStatPresenter";
import DualPeriodComparison from "../../components/DualPeriodComparison";
import moment from "moment";
import {executeQueryUtil} from "../../utils/gqlUtil";
import {getSumUpDayQuery} from "../../gql/SumUpDayGql";
import {getSumUpWeekQuery} from "../../gql/SumUpWeekGql";
import {getSumUpMonthQuery} from "../../gql/SumUpMonthGql";
import {filterWeek, filterMonth, filterSingleDay} from "../Home/FilterInfoOverview";
import {decompressFromBase64} from "../../utils/compressUtil";
// Note: getWeekKey and getMonthKey functions are defined locally since they don't exist in frontend DateUtil

// Helper functions to match backend dateUtil format
// 📅 ATTENTION: Ces fonctions seront déplacées vers DateUtil.js dans le futur
// Pour l'instant, elles restent locales pour éviter les conflits avec le backend
const getWeekKey = (date) => {
    const weekStart = moment(date).startOf('isoWeek');
    const isoWeekNumber = weekStart.isoWeek();
    return `${weekStart.format('YYYY')}-W${String(isoWeekNumber).padStart(2, '0')}-${weekStart.format('DD-MM')}`;
};

const getMonthKey = (dayMoment) => {
    const monthStart = moment(dayMoment).startOf('month');
    const monthEnd = moment(dayMoment).endOf('month');
    return `${monthStart.format('YYYY')}-M${monthStart.format('MM')}-${monthEnd.format('DD')}-${monthEnd.format('MM')}`;
};

function TurnOverSpark({modeGraph}) {
    const [sumUpDaysStructured, setSumUpDaysStructured] = useState(null);
    const [chartOptions, setChartOptions] = useState(null)
    const [totalTurnover, setTotalTurnover] = useState(0)
    const [comparisonData, setComparisonData] = useState(null)
    const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"));

    const {
        sumUpDays,
        categories,
        staffs,
        paymentModes,
        locations,
        products,
        optionLists,
        weatherDays,
        filter,
        currentEstablishment,
        currentBrand,
        loading,
    } = useAuth();


    useEffect(() => {
        if (!sumUpDays || sumUpDays.length === 0) {
            return;
        }
        if (!products || !staffs || !paymentModes || !locations || !categories) {
            return;
        }

        const structuredSumup = fillNamesOfSumDays(sumUpDays, staffs, locations,
            products, optionLists, paymentModes, categories);
        setSumUpDaysStructured(structuredSumup);

    }, [sumUpDays, products, staffs, paymentModes, locations, categories, optionLists])



    useEffect(() => {

            const fillData = async () => {
                // setLoading(true);
                if (!sumUpDaysStructured || !sumUpDays || !products || !staffs || !paymentModes || !locations || !categories) {
                    return;
                }
                try {

                    const res = await getApexChartData(
                        sumUpDaysStructured, weatherDays, filter
                    );

                    if (res) {
                        setChartOptions(res);
                    }

                } catch (e) {
                    console.error('Erreur lors de la récupération des données pour le graphique :', e);

                }
            }
            fillData();
        }
        , [sumUpDays, products, staffs, paymentModes, locations, categories, sumUpDaysStructured, filter])

    // Separate useEffect for comparison data that refreshes when filter changes
    useEffect(() => {
        const loadComparisonData = async () => {
            console.log('🔄 loadComparisonData triggered', {
                filter: filter,
                sumUpDaysStructured: sumUpDaysStructured,
                brandId: currentBrand()?.id,
                establishmentId: currentEstablishment()?.id
            });

            if (!filter || !currentBrand()?.id || !currentEstablishment()?.id || !sumUpDaysStructured) {
                console.log('⚠️ Missing required data for comparison', {
                    hasFilter: !!filter,
                    hasBrand: !!currentBrand()?.id,
                    hasEstablishment: !!currentEstablishment()?.id,
                    hasSumUpDays: !!sumUpDaysStructured
                });
                return;
            }

            // Calculate current total turnover from sumUpDaysStructured
            let currentTotalTurnover = 0;
            if (filter?.filterType === filterSingleDay && sumUpDaysStructured.length > 0) {
                // For single day, we need to calculate from products
                const allProducts = await getAllProductsGlobal(sumUpDaysStructured, currentEstablishment());
                allProducts.forEach((product) => {
                    currentTotalTurnover += product.totalPrice || 0;
                });
            } else {
                // For week/month, sum up all days
                for (const sumDay of sumUpDaysStructured) {
                    currentTotalTurnover += sumDay?.totalTurnover || 0;
                }
            }

            console.log('💰 Current total turnover calculated:', {
                value: currentTotalTurnover,
                daysCount: sumUpDaysStructured?.length,
                filterType: filter?.filterType,
                method: filter?.filterType === filterSingleDay ? 'products' : 'sumDays'
            });

            try {
                if (filter?.filterType === filterWeek) {
                    // For week filter, compare with previous week and previous month same week
                    // Use shared dateUtil functions for consistency
                    // Utilise l'utilitaire getFilterMoment pour une conversion cohérente
                    const filterMoment = getFilterMoment(filter);
                    const currentWeek = getWeekKey(filterMoment);
                    const previousWeek = getWeekKey(filterMoment.clone().subtract(1, 'week'));
                    const previousMonthWeek = getWeekKey(filterMoment.clone().subtract(1, 'month'));

                    console.log('Week comparison (FIXED - using filter.startDate):', {
                        current: currentWeek,
                        previousWeek: previousWeek,
                        previousMonth: previousMonthWeek,
                        brandId: currentBrand()?.id,
                        establishmentId: currentEstablishment()?.id,
                        filterStartDate: filter.startDate,
                        filterSingleDay: filter.singleDay
                    });

                    const [previousWeekResult, previousMonthResult] = await Promise.all([
                        executeQueryUtil(getSumUpWeekQuery(currentBrand()?.id, currentEstablishment()?.id, previousWeek)),
                        executeQueryUtil(getSumUpWeekQuery(currentBrand()?.id, currentEstablishment()?.id, previousMonthWeek))
                    ]);

                    console.log('Week results (from separate useEffect):', { previousWeekResult, previousMonthResult });

                    // Decode previousWeek data
                    let previousWeekContent = previousWeekResult?.data?.getSumUpWeek?.content;
                    previousWeekContent = previousWeekContent && decompressFromBase64(previousWeekContent);
                    const previousWeekParsed = previousWeekContent ? JSON.parse(previousWeekContent) : null;
                    const previousWeekTotal = previousWeekParsed?.totalTurnover || null;

                    // Decode previousMonth data
                    let previousMonthContent = previousMonthResult?.data?.getSumUpWeek?.content;
                    previousMonthContent = previousMonthContent && decompressFromBase64(previousMonthContent);
                    const previousMonthParsed = previousMonthContent ? JSON.parse(previousMonthContent) : null;
                    const previousMonthTotal = previousMonthParsed?.totalTurnover || null;

                    setComparisonData({
                        current: currentTotalTurnover,
                        previousWeek: previousWeekTotal,
                        previousMonth: previousMonthTotal,
                        currentDate: filter.singleDay
                    });

                } else if (filter?.filterType === filterMonth) {
                    // For month filter, compare with previous month AND same month of previous year
                    // Use shared dateUtil functions for consistency
                    // Utilise l'utilitaire getFilterMoment pour une conversion cohérente
                    const filterMoment = getFilterMoment(filter);
                    const currentMonth = getMonthKey(filterMoment);
                    const previousMonth = getMonthKey(filterMoment.clone().subtract(1, 'month'));
                    const previousYearMonth = getMonthKey(filterMoment.clone().subtract(1, 'year'));

                    console.log('Month comparison (FIXED - using filter.startDate):', {
                        current: currentMonth,
                        previousMonth: previousMonth,
                        previousYear: previousYearMonth,
                        brandId: currentBrand()?.id,
                        establishmentId: currentEstablishment()?.id,
                        filterStartDate: filter.startDate,
                        filterSingleDay: filter.singleDay
                    });

                    // 🔍 DEBUG: Récupérer aussi les données du mois actuel depuis la base pour comparer
                    const [currentMonthFromDB, previousMonthResult, previousYearResult] = await Promise.all([
                        executeQueryUtil(getSumUpMonthQuery(currentBrand()?.id, currentEstablishment()?.id, currentMonth)),
                        executeQueryUtil(getSumUpMonthQuery(currentBrand()?.id, currentEstablishment()?.id, previousMonth)),
                        executeQueryUtil(getSumUpMonthQuery(currentBrand()?.id, currentEstablishment()?.id, previousYearMonth))
                    ]);

                    // Decode current month from DB for comparison
                    let currentMonthDBContent = currentMonthFromDB?.data?.getSumUpMonth?.content;
                    currentMonthDBContent = currentMonthDBContent && decompressFromBase64(currentMonthDBContent);
                    const currentMonthDBParsed = currentMonthDBContent ? JSON.parse(currentMonthDBContent) : null;
                    const currentMonthDBTotal = currentMonthDBParsed?.totalTurnover || null;

                    console.log('🔍 COMPARAISON Mois Actuel:', {
                        calculéLocalement: currentTotalTurnover,
                        depuisDB: currentMonthDBTotal,
                        différence: currentTotalTurnover - (currentMonthDBTotal || 0),
                        problème: Math.abs(currentTotalTurnover - (currentMonthDBTotal || 0)) > 1
                    });


                    console.log('Month results (from separate useEffect):', { previousMonthResult, previousYearResult });

                    // Decode previousMonth data
                    let previousMonthContent = previousMonthResult?.data?.getSumUpMonth?.content;
                    previousMonthContent = previousMonthContent && decompressFromBase64(previousMonthContent);
                    const previousMonthParsed = previousMonthContent ? JSON.parse(previousMonthContent) : null;
                    const previousMonthTotal = previousMonthParsed?.totalTurnover || null;

                    // DEBUG: Log the parsed data to understand what's being retrieved
                    console.log('🔍 DEBUG Mode Mois - Previous Month Data:', {
                        key: previousMonth,
                        parsedData: previousMonthParsed,
                        totalTurnover: previousMonthTotal,
                        hasContent: !!previousMonthContent
                    });

                    // Decode previousYear data
                    let previousYearContent = previousYearResult?.data?.getSumUpMonth?.content;
                    previousYearContent = previousYearContent && decompressFromBase64(previousYearContent);
                    const previousYearParsed = previousYearContent ? JSON.parse(previousYearContent) : null;
                    const previousYearTotal = previousYearParsed?.totalTurnover || null;

                    // 🔍 DEBUG: Si les données de la DB sont différentes, utiliser celles-ci pour la cohérence
                    const finalCurrentTotal = currentMonthDBTotal !== null && Math.abs(currentTotalTurnover - currentMonthDBTotal) > 1
                        ? currentMonthDBTotal  // Utiliser les données de la DB pour la cohérence
                        : currentTotalTurnover; // Utiliser le calcul local

                    console.log('📊 Décision finale pour le mois actuel:', {
                        utilisé: finalCurrentTotal,
                        source: finalCurrentTotal === currentMonthDBTotal ? 'DB' : 'Local'
                    });

                    setComparisonData({
                        current: finalCurrentTotal,
                        previousMonth: previousMonthTotal,
                        previousYear: previousYearTotal,
                        currentDate: filter.singleDay
                    });

                } else if (filter?.filterType === filterSingleDay) {
                    // Utilise l'utilitaire getFilterMoment pour une conversion cohérente
                    const currentDate = getFilterMoment(filter);
                    const previousDate = currentDate.clone().subtract(1, 'day').format('YYYY-MM-DD');
                    const previousWeekDate = currentDate.clone().subtract(7, 'days').format('YYYY-MM-DD');
                    const previousMonthDate = currentDate.clone().subtract(1, 'month').format('YYYY-MM-DD');

                    const [previousDayResult, previousWeekResult, previousMonthResult] = await Promise.all([
                        executeQueryUtil(getSumUpDayQuery(currentBrand()?.id, currentEstablishment()?.id, previousDate)),
                        executeQueryUtil(getSumUpDayQuery(currentBrand()?.id, currentEstablishment()?.id, previousWeekDate)),
                        executeQueryUtil(getSumUpDayQuery(currentBrand()?.id, currentEstablishment()?.id, previousMonthDate))
                    ]);

                    const previousDayTotal = previousDayResult?.data?.getSumUpDay
                        ? JSON.parse(previousDayResult.data.getSumUpDay.content).totalTurnover || 0
                        : null;

                    const previousWeekTotal = previousWeekResult?.data?.getSumUpDay
                        ? JSON.parse(previousWeekResult.data.getSumUpDay.content).totalTurnover || 0
                        : null;

                    const previousMonthTotal = previousMonthResult?.data?.getSumUpDay
                        ? JSON.parse(previousMonthResult.data.getSumUpDay.content).totalTurnover || 0
                        : null;

                    setComparisonData({
                        current: currentTotalTurnover,
                        previousDay: previousDayTotal,
                        previousWeek: previousWeekTotal,
                        previousMonth: previousMonthTotal,
                        currentDate: filter.singleDay
                    });
                }
            } catch (error) {
                console.error('Error loading comparison data:', error);
                setComparisonData(null);
            }
        };

        loadComparisonData();
    }, [filter?.singleDay, filter?.filterType, sumUpDaysStructured, currentBrand, currentEstablishment]) // FIXED: Use specific filter properties

    async function getTotalTurnOver(sumUpDaysStructured, weatherDays, filter) {
        if (!filter) {
            return;
        }

        if (!sumUpDaysStructured || sumUpDaysStructured.length === 0) {
            return;
        }
        let totalTurnover = 0;
        let data = [];
        let sortedSlots = [];
        if (filter?.filterType === "filterSingleDay") {


            let timeSlotTotals = {};
            const allProducts = await getAllProductsGlobal(sumUpDaysStructured, currentEstablishment());

            allProducts.forEach((product) => {
                const timeSlot = product.timeSlot || "00:00";
                if (!timeSlotTotals[timeSlot]) timeSlotTotals[timeSlot] = 0;
                timeSlotTotals[timeSlot] += product.totalPrice || 0;
            });

// 🔽 Tri des créneaux horaires
            sortedSlots = Object.keys(timeSlotTotals).sort(); // ["08:00", "09:00", ...]
            data = sortedSlots.map((slot) => timeSlotTotals[slot]);

// 🔽 Calcul du chiffre d'affaires total
            totalTurnover = data.reduce((sum, val) => sum + val, 0);
            return totalTurnover;
        }
    }

    async function getApexChartData(sumUpDaysStructured, weatherDays, filter) {

        if (!filter) {
            return;
        }

        if (!sumUpDaysStructured || sumUpDaysStructured.length === 0) {
            return;
        }
        let totalTurnover = 0;
        let data = [];
        let sortedSlots = [];
        if (filter?.filterType === "filterSingleDay") {


            let timeSlotTotals = {};
            const allProducts = await getAllProductsGlobal(sumUpDaysStructured, currentEstablishment());

            allProducts.forEach((product) => {
                const timeSlot = product.timeSlot || "00:00";
                if (!timeSlotTotals[timeSlot]) timeSlotTotals[timeSlot] = 0;
                timeSlotTotals[timeSlot] += product.totalPrice || 0;
            });

// 🔽 Tri des créneaux horaires
            sortedSlots = Object.keys(timeSlotTotals).sort(); // ["08:00", "09:00", ...]
            data = sortedSlots.map((slot) => timeSlotTotals[slot]);

// 🔽 Calcul du chiffre d'affaires total
            totalTurnover = data.reduce((sum, val) => sum + val, 0);
            setTotalTurnover(totalTurnover);
        }

        else {

            sortedSlots = sumUpDaysStructured.map((sumDay) => sumDay.date);
            for (const sumDay of sumUpDaysStructured) {

                data.push(sumDay?.totalTurnover || 0);
                totalTurnover += sumDay?.totalTurnover || 0;
            }
            setTotalTurnover(totalTurnover);
        }



// 🔁 Résultat à retourner
        return {
            series: [
                {
                    name: localStrings.turnover,
                    data,
                },
            ],
            options: {
                chart: {
                    id: 'sparkline-turnover',
                    type: 'area',
                    height: 260,
                    sparkline: {
                        enabled: false, // Disable sparkline to show x-axis
                    },
                    toolbar: {
                        show: false
                    }
                },
                stroke: {
                    curve: 'straight',
                    width: 2,
                },
                fill: {
                    opacity: 0.3,
                },
                dataLabels: {
                    enabled: false
                },
                grid: {
                    show: false, // Hide grid lines
                },
                yaxis: {
                    min: 0,
                    labels: {
                        show: false, // Keep Y axis hidden
                    },
                },
                xaxis: {
                    type: "category",
                    categories: sortedSlots, // Ex: ["08:00", "09:00", ...]
                    labels: {
                        show: true,
                        rotate: -45,
                        rotateAlways: true,
                        style: {
                            fontSize: '11px'
                        },
                        hideOverlappingLabels: true,
                    },
                    axisBorder: {
                        show: false
                    },
                    axisTicks: {
                        show: false
                    }
                },
                labels: sortedSlots,
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return formatEurPriceAndRoundUtil(val, lgUp); // Affichage € correctement
                        },
                    },
                    x: {
                        formatter: function (val, opts) {
                            const isIndex = typeof val === 'number' && sortedSlots[val - 1];
                            return isIndex ? sortedSlots[val - 1] : val;
                        }
                    }
                },
                colors: ['#DCE6EC'],
                title: {
                    text: formatEurPriceAndRoundUtil(totalTurnover, lgUp),
                    offsetX: 30,
                    style: {
                        fontSize: '24px',
                        cssClass: 'apexcharts-yaxis-title',
                    },
                },
                subtitle: {
                    text: localStrings.turnover,
                    offsetX: 30,
                    style: {
                        fontSize: '14px',
                        cssClass: 'apexcharts-yaxis-title',
                    },
                }
            }
        };

    }

    return (
        <Card sx={{margin: 0, padding: 0}}>

            {/*<p>{JSON.stringify(filter || {})}</p>*/}

            {/*<p>{loading ? "Y" : "N"}</p>*/}
            <CardContent sx={{margin: 0, padding: 0, paddingBottom: "0 !important"}}>
                {!loading ?
                    <>
                        {sumUpDaysStructured && sumUpDaysStructured.length > 0 && chartOptions  &&
                            <>
                                {modeGraph ?
                                    <>
                                        {comparisonData && (
                                            <>
                                                <DualPeriodComparison
                                                    currentValue={comparisonData.current}
                                                    previousDayValue={comparisonData.previousDay}
                                                    previousWeekValue={comparisonData.previousWeek}
                                                    previousMonthValue={comparisonData.previousMonth}
                                                    previousYearValue={comparisonData.previousYear}
                                                    currentDate={comparisonData.currentDate}
                                                    formatter={(val) => formatEurPriceAndRoundUtil(val, lgUp)}
                                                    invertColors={false}
                                                />
                                            </>
                                        )}
                                        <ApexChart
                                            options={chartOptions.options}
                                            series={chartOptions.series}
                                            type={chartOptions.options?.chart?.type}
                                            height={300}
                                        />
                                    </>
                                    :
                                    <>
                                        {comparisonData && (
                                            <DualPeriodComparison
                                                currentValue={comparisonData.current}
                                                previousDayValue={comparisonData.previousDay}
                                                previousWeekValue={comparisonData.previousWeek}
                                                previousMonthValue={comparisonData.previousMonth}
                                                previousYearValue={comparisonData.previousYear}
                                                currentDate={comparisonData.currentDate}
                                                formatter={(val) => formatEurPriceAndRoundUtil(val, lgUp)}
                                                invertColors={false}
                                            />
                                        )}
                                        <StackStatPresenter
                                            backgroundColor={(theme) => theme.palette.mode === 'dark'
                                                ? 'neutral.800'
                                                : 'error.lightest'}
                                            icon={<EuroIcon />}
                                            value={formatEurPriceAndRoundUtil(totalTurnover, lgUp)}
                                            label={localStrings.turnover}
                                        />
                                    </>
                                }
                            </>

                        }
                    </>
                    :
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '350px'
                    }}>
                        <CircularProgress size="100px" />
                    </Box>
                }
            </CardContent>
        </Card>
    )
}

export default TurnOverSpark